import { useEffect, useState } from 'react';
import CompanyService from '../../services/CompanyService';
import { TOKEN_KEY } from '../../services/api';

// The firm's logo as an object URL, shared by the sidebar, the company page and printouts. Loaded once per login
// (cache keyed by the token) and refreshed for every user of the hook after an upload or removal.
let cache = null; // { token, promise, url }
const listeners = new Set();

const load = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!cache || cache.token !== token) {
        if (cache?.url) URL.revokeObjectURL(cache.url);
        cache = { token, url: null };
        cache.promise = CompanyService.get()
            .then(c => (c.hasLogo ? CompanyService.logoBlob().then(b => URL.createObjectURL(b)) : null))
            .catch(() => null)
            .then(url => { if (cache?.token === token) cache.url = url; return url; });
    }
    return cache.promise;
};

/** Call after the logo changed: every component using the hook reloads it. */
export function refreshCompanyLogo() {
    if (cache?.url) URL.revokeObjectURL(cache.url);
    cache = null;
    listeners.forEach(fn => fn());
}

/** Object URL of the company logo, or null (none uploaded / still loading). */
export function useCompanyLogo() {
    const [url, setUrl] = useState(() => cache?.url ?? null);
    const [version, setVersion] = useState(0);
    useEffect(() => {
        const bump = () => setVersion(v => v + 1);
        listeners.add(bump);
        return () => listeners.delete(bump);
    }, []);
    useEffect(() => {
        let alive = true;
        load().then(u => { if (alive) setUrl(u); });
        return () => { alive = false; };
    }, [version]);
    return url;
}
